import React, { useEffect, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  Grid,
  Hidden,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Switch,
  FormControlLabel,
  Typography,
  InputAdornment,
  Card,
  CardContent,
  CardActions,
  Divider,
  Chip,
  Tooltip,
  Box,
  Avatar
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { Field, Form, Formik } from 'formik';
import InputMask from 'react-input-mask';
import SearchIcon from '@material-ui/icons/Search';
import VpnKeyIcon from '@material-ui/icons/VpnKey';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import FilterListIcon from '@material-ui/icons/FilterList';
import PersonIcon from '@material-ui/icons/Person';
import EditIcon from '@material-ui/icons/Edit';
import LinkIcon from '@material-ui/icons/Link';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import ScheduleIcon from '@material-ui/icons/Schedule';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useTheme, makeStyles, fade } from '@material-ui/core/styles';
import * as Yup from 'yup';
import useApi from '../../../utils/useApi';
import UIButtonSearch from '../../UI/Button/Search/Search';
import UIModalFilter from '../../UI/ModalFilter/UIModalFilter';
import UIContainer from '../../UI/Container/UIContainer';
import UIFormTitle from '../../UI/Form/Title/UIFormTitle';
import { emptyObject } from '../../../utils/utils';
import UIDialogConfirm from '../../UI/Dialog/Confirm/UIDialogConfirm';
import UIDialogTitle from '../../UI/Dialog/Title/UIDialogTitle';
import GreenSwitch from '../../UI/Form/Switch/GreenSwitch';
import useStylesOriginal from '../../../utils/useStyle';

const usePageStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: '#f4f6f8',
    minHeight: '100vh',
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
  },
  card: {
    borderRadius: 16,
    boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)',
    border: '1px solid rgba(0,0,0,0.05)',
    marginBottom: theme.spacing(3),
    overflow: 'hidden',
  },
  cardHeader: {
    padding: theme.spacing(2, 3),
    borderBottom: '1px solid #f0f0f0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#fff',
  },
  filterSection: {
    padding: theme.spacing(3),
  },
  tableContainer: {
    boxShadow: 'none',
  },
  tableHead: {
    backgroundColor: '#f9fafb',
  },
  tableHeadCell: {
    color: '#6b778c',
    fontWeight: 600,
    textTransform: 'uppercase',
    fontSize: '0.75rem',
    letterSpacing: '0.05em',
  },
  tableRow: {
    '&:hover': {
      backgroundColor: '#fcfcfc !important',
    },
  },
  chipOperator: {
    backgroundColor: fade(theme.palette.info.main, 0.1),
    color: theme.palette.info.main,
    fontWeight: 600,
    borderRadius: 8,
  },
  chipManager: {
    backgroundColor: fade(theme.palette.warning.main, 0.1),
    color: theme.palette.warning.dark,
    fontWeight: 600,
    borderRadius: 8,
  },
  chipAdmin: {
    backgroundColor: fade(theme.palette.error.main, 0.1),
    color: theme.palette.error.main,
    fontWeight: 600,
    borderRadius: 8,
  },
  avatar: {
    backgroundColor: theme.palette.primary.main,
    width: 32,
    height: 32,
    fontSize: '0.875rem',
    marginRight: theme.spacing(1),
  },
  actionButton: {
    padding: 8,
    marginLeft: 4,
    backgroundColor: '#f5f5f5',
    '&:hover': {
      backgroundColor: '#e0e0e0',
    },
  },
  searchInput: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 12,
      backgroundColor: '#f9fafb',
      '& fieldset': {
        borderColor: '#e0e0e0',
      },
      '&:hover fieldset': {
        borderColor: theme.palette.primary.light,
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main,
      },
    },
  },
  dialogPaper: {
    borderRadius: 16,
  },
}));

const DURATION_OPTIONS = [
  { value: '900', label: '15 minutos', chipLabel: '15 min' },
  { value: '3600', label: '1 hora', chipLabel: '1 h' },
  { value: '21600', label: '6 horas', chipLabel: '6 h' },
  { value: '43200', label: '12 horas', chipLabel: '12 h' },
  { value: '86400', label: '24 horas (1 dia)', chipLabel: '24 h', recommended: true },
  { value: '259200', label: '3 dias', chipLabel: '3 dias' },
  { value: '604800', label: '7 dias (1 semana)', chipLabel: '7 dias' },
  { value: 'custom', label: 'Personalizado...', chipLabel: 'Personalizado' },
];

const initialState = {
  id: '',
  name: '',
  lastName: '',
  cpf: '',
  username: '',
  children: 0,
  profile: '',
  cashRegisters: [],
};

const passwordSchema = Yup.object().shape({
  password: Yup.string()
    .min(6, 'A senha deve ter no mínimo 6 caracteres')
    .required('Campo obrigatório'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password'), null], 'As senhas devem ser iguais')
    .required('Campo obrigatório'),
});

const profiles = {
  1: 'Operador',
  2: 'Gerente',
  3: 'Administrador',
};

const initialClick = {
  mouseX: null,
  mouseY: null,
};

const UserSearch = (children) => {
  const { referenceId } = children;

  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { disabledTextField } = useStylesOriginal();
  const classes = usePageStyles();

  const [alert, setAlert] = useState(false);

  const [view, setView] = useState(!!referenceId);
  const [filter, setFilter] = useState(false);
  const [modalValues, setModalValues] = useState(null);

  const [passwordModal, setPasswordModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [userActive, setUserActive] = useState(false);
  const [isActive, setIsActive] = useState('1');

  const [search, setSearch] = useState(null);

  const [linkModal, setLinkModal] = useState(false);
  const [generatedUrl, setGeneratedUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const [targetUser, setTargetUser] = useState(null);
  const [linkDuration, setLinkDuration] = useState('86400');
  const [customDurationValue, setCustomDurationValue] = useState(1);
  const [customDurationUnit, setCustomDurationUnit] = useState('days');
  const [generatedDuration, setGeneratedDuration] = useState(86400);

  const [generateLinkLoad, generateLinkInfo] = useApi({
    method: 'get',
    url: '/auth/signed-url',
  });

  const calculateDurationSeconds = () => {
    if (linkDuration === 'custom') {
      const val = Math.max(1, parseInt(customDurationValue, 10) || 1);
      if (customDurationUnit === 'minutes') return Math.min(2592000, Math.max(60, val * 60));
      if (customDurationUnit === 'hours') return Math.min(2592000, Math.max(60, val * 3600));
      if (customDurationUnit === 'days') return Math.min(2592000, Math.max(60, val * 86400));
      return 86400;
    }
    return parseInt(linkDuration, 10) || 86400;
  };

  const getDurationLabel = (seconds) => {
    const found = DURATION_OPTIONS.find((opt) => opt.value === String(seconds));
    if (found && found.value !== 'custom') return found.label;
    if (seconds < 3600) return `${Math.round(seconds / 60)} minuto(s)`;
    if (seconds < 86400) return `${Math.round(seconds / 3600)} hora(s)`;
    return `${Math.round(seconds / 86400)} dia(s)`;
  };

  const handleOpenLinkModal = (user) => {
    const userToUse = user || modalValues;
    if (!userToUse || !userToUse.id) return;
    setTargetUser(userToUse);
    setGeneratedUrl('');
    setCopied(false);
    setLinkModal(true);
  };

  const handleGenerateLink = (durationToUse) => {
    const userToUse = targetUser || modalValues;
    if (!userToUse || !userToUse.id) return;

    let seconds = 86400;
    if (durationToUse !== undefined) {
      seconds = typeof durationToUse === 'string' ? parseInt(durationToUse, 10) : durationToUse;
    } else {
      seconds = calculateDurationSeconds();
    }

    setCopied(false);
    generateLinkLoad({
      params: {
        userId: userToUse.id,
        expiresIn: seconds,
      },
      onCompleted: (res) => {
        if (res && res.data && res.data.token) {
          const url = `${window.location.origin}/?loginToken=${res.data.token}`;
          setGeneratedUrl(url);
          setGeneratedDuration(seconds);
        }
      },
    });
  };

  const [cashierLoad, cashierInfo] = useApi({
    method: 'get',
    url: '/cash-registers',
  });

  const [profileLoad, profileInfo] = useApi({
    method: 'get',
    url: '/users/profiles',
  });

  const [filterLoad, filterInfo] = useApi({
    debounceDelay: 500,
    method: 'get',
    url: '/users',
    params: { isActive: '1' },
  });

  const [updateLoad, updateInfo, setUpdateInfo] = useApi({
    method: 'put',
    url: '/users',
    onCompleted: (res) => {
      if (res) {
        filterLoad({
          params: search,
        });
      }
    },
  });

  const [activeLoad, activeInfo, setActiveInfo] = useApi({
    method: 'patch',
    url: '/users',
    onCompleted: () => {
      filterLoad({
        params: search,
      });
    },
  });

  const [changePassLoad, changePassInfo, setChangePassInfo] = useApi({
    method: 'put',
    url: '/users',
    onCompleted: () => {
      setAlert(true);
    }
  });

  function humanAttributes(values) {
    if (!values) return;

    const { cashRegisters } = values;
    const cashierData = cashierInfo.data || [];

    // Tratamento seguro para cashRegisters
    const cashRegistersTraeted = (Array.isArray(cashRegisters) ? cashRegisters : [])
      .map((value) => {
        // Se for objeto (já populado)
        if (typeof value === 'object' && value !== null) {
          const found = cashierData.find((val) => Number(val.id) === Number(value.id));
          return found || value;
        }
        // Se for ID
        return cashierData.find((val) => Number(val.id) === Number(value));
      })
      .filter((item) => item !== undefined && item !== null); // Filtra itens indefinidos para evitar o erro

    const data = {
      ...values,
      isActive: values.isActive == 1 ? 1 : 0,
      cashRegisters: cashRegistersTraeted,
    };
    setModalValues(data);
  }

  const [clickRight, setClickRight] = useState(initialClick);
  const handleClickRight = (event, values) => {
    event.preventDefault();
    humanAttributes(values);
    setClickRight({
      mouseX: event.clientX - 2,
      mouseY: event.clientY - 4,
    });
  };
  const handleCloseClickRight = () => {
    setClickRight(initialClick);
  };

  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleClickLeft = (event, values) => {
    humanAttributes(values);
    setAnchorEl(event.currentTarget);
  };

  const handleCloseClickLeft = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    cashierLoad();
    profileLoad();
    filterLoad();
  }, []);

  function handleModalFilter() {
    setFilter(!filter);
  }

  function handleModalActive(values, isClick) {
    if (values) {
      humanAttributes(values);
    }
    if (isClick) {
      const closeMenu = mobile ? handleCloseClickLeft : handleCloseClickRight;
      closeMenu();
    }
    setUserActive(!userActive);
  }

  function handleModalUpdate(values, isClick) {
    if (values) {
      humanAttributes(values);
    }
    if (isClick) {
      const closeMenu = mobile ? handleCloseClickLeft : handleCloseClickRight;
      closeMenu();
    }
    setView(!view);
  }

  function handleModalPassword(values, isClick) {
    if (values) {
      humanAttributes(values);
    }
    if (isClick) {
      const closeMenu = mobile ? handleCloseClickLeft : handleCloseClickRight;
      closeMenu();
    }
    setPasswordModal(!passwordModal);
    setShowPassword(false);
  }

  function fullName(data) {
    return data ? `${data.name} ${data.lastName || ''}` : '';
  }

  function closeAlert(event, reason) {
    setActiveInfo();
    setUpdateInfo();
    setChangePassInfo();
    if (reason === 'clickaway') {
      return;
    }
    setAlert(false);
  }

  function onSubmit(values) {
    const active = values.isActive || isActive;
    const searchValues = {
      ...values,
      isActive: active,
    };
    filterLoad({
      debounced: true,
      params: emptyObject(searchValues),
    });
    setSearch(emptyObject(searchValues));
  }

  function onUpdate(values) {
    const updateValues = {
      id: values.id,
      cashRegisters: values.cashRegisters.map(({ id }) => id),
      name: values.name,
      lastName: values.lastName,
      profile: values.profile,
    };
    updateLoad({
      data: updateValues,
    });
    setView(!view);
    setAlert(true);
  }

  function onUpdatePassword(values) {
    changePassLoad({
      data: {
        id: modalValues.id,
        password: values.password
      }
    });
    setPasswordModal(false);
  }

  function onActive() {
    const data = { id: modalValues.id };
    activeLoad({ data, onCompleted: () => setAlert(true) });
    setModalValues(null);
  }

  function getProfileName(profileId) {
    if (!profileId) return 'Indefinido';
    if (profileInfo.data) {
      const found = profileInfo.data.find(
        (p) => Number(p.id) === Number(profileId)
      );
      if (found) return found.name;
    }
    const legacy = { 1: 'Operador', 2: 'Gerente', 3: 'Administrador' };
    return legacy[profileId] || `Perfil ${profileId}`;
  }

  function getProfileChip(profileId) {
    const name = getProfileName(profileId);
    if (profileId == 1) return <Chip label={name} size="small" className={classes.chipOperator} />;
    if (profileId == 2) return <Chip label={name} size="small" className={classes.chipManager} />;
    if (profileId == 3) return <Chip label={name} size="small" className={classes.chipAdmin} />;
    return <Chip label={name} size="small" />;
  }

  return (
    <div className={classes.root}>
      <Formik onSubmit={onSubmit} initialValues={initialState}>
        {({ values, submitForm, setFieldValue }) => (
          <Form onChange={submitForm}>
            <UIContainer>
              <Card className={classes.card}>
                <div className={classes.cardHeader}>
                  <Box display="flex" alignItems="center">
                    <FilterListIcon color="primary" style={{ marginRight: 8 }} />
                    <Typography variant="h6" color="textPrimary">
                      Filtros de Pesquisa
                    </Typography>
                  </Box>
                  <Button
                    startIcon={<FilterListIcon />}
                    onClick={handleModalFilter}
                    color="primary"
                    style={{ fontWeight: 600 }}
                  >
                    Filtros Avançados
                  </Button>
                </div>

                <CardContent className={classes.filterSection}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={1}>
                      <Field
                        name="id"
                        placeholder="ID"
                        fullWidth
                        as={TextField}
                        variant="outlined"
                        size="small"
                        className={classes.searchInput}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">#</InputAdornment>,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Field
                        name="name"
                        placeholder="Nome do usuário"
                        fullWidth
                        as={TextField}
                        variant="outlined"
                        size="small"
                        className={classes.searchInput}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon color="action" fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Hidden smDown>
                      <Grid item xs={12} md={3}>
                        <Field
                          name="lastName"
                          placeholder="Sobrenome"
                          fullWidth
                          as={TextField}
                          variant="outlined"
                          size="small"
                          className={classes.searchInput}
                        />
                      </Grid>
                    </Hidden>
                    <Grid item xs={12} md={3}>
                      <Field
                        name="username"
                        placeholder="Login/Username"
                        fullWidth
                        as={TextField}
                        variant="outlined"
                        size="small"
                        className={classes.searchInput}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PersonIcon color="action" fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Hidden smDown>
                      <Grid item xs={12} md={2}>
                        <InputMask
                          value={values.cpf}
                          mask="999.999.999-99"
                          onChange={(ev) =>
                            setFieldValue('cpf', ev.target.value.replace(/[^0-9]/g, ''))
                          }
                        >
                          {() => (
                            <TextField
                              placeholder="CPF"
                              fullWidth
                              variant="outlined"
                              size="small"
                              className={classes.searchInput}
                            />
                          )}
                        </InputMask>
                      </Grid>
                    </Hidden>
                  </Grid>
                </CardContent>
              </Card>

              <UIModalFilter
                open={filter}
                changeModal={handleModalFilter}
                submit={submitForm}
              >
                <Hidden mdUp>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>Filtros Mobile</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <InputMask
                      value={values.cpf}
                      mask="999.999.999-99"
                      onChange={(ev) =>
                        setFieldValue(
                          'cpf',
                          ev.target.value.replace(/[^0-9]/g, ''),
                        )
                      }
                    >
                      {() => (
                        <TextField
                          label="CPF"
                          placeholder="Informe o numero do CPF"
                          fullWidth
                          variant="outlined"
                        />
                      )}
                    </InputMask>
                  </Grid>
                  <Grid item xs={12}>
                    <Field
                      name="lastName"
                      label="Sobrenome"
                      placeholder="Informe o sobrenome"
                      fullWidth
                      as={TextField}
                      variant="outlined"
                    />
                  </Grid>
                </Hidden>
                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    disabled={cashierInfo.loading}
                    options={cashierInfo.data || []}
                    value={values.cashRegisters}
                    getOptionLabel={(cash) => (cash && cash.name) ? cash.name : ''}
                    onChange={(event, cash) =>
                      setFieldValue('cashRegisters', cash)
                    }
                    getOptionSelected={(op, cash) => op.name === (cash ? cash.name : '')}
                    noOptionsText="Sem opções"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Operador do caixa"
                        placeholder="Adicionar mais"
                        variant="outlined"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel shrink id="users-search-profile">
                      Perfil
                    </InputLabel>
                    <Field
                      name="profile"
                      labelId="users-search-profile"
                      disabled={profileInfo.loading}
                      displayEmpty
                      fullWidth
                      as={Select}
                      label="Perfil"
                    >
                      <MenuItem value="">
                        <em>Todos os perfis</em>
                      </MenuItem>
                      {profileInfo.data &&
                        profileInfo.data.map((prof) => (
                          <MenuItem key={prof.id} value={prof.id}>
                            {prof.name}
                          </MenuItem>
                        ))}
                    </Field>
                  </FormControl>
                </Grid>
              </UIModalFilter>
            </UIContainer>
          </Form>
        )}
      </Formik>

      <UIContainer>
        <Card className={classes.card}>
          <div className={classes.cardHeader}>
            <Box display="flex" alignItems="center">
              <Typography variant="h6" style={{ fontWeight: 600 }}>
                Usuários Cadastrados
              </Typography>
              <Chip
                label={filterInfo.data ? filterInfo.data.length : 0}
                size="small"
                color="primary"
                style={{ marginLeft: 8, borderRadius: 6 }}
              />
            </Box>
            <FormControlLabel
              control={
                <GreenSwitch
                  checked={isActive == 1}
                  onChange={(ev) => {
                    setIsActive(ev.target.checked ? '1' : '0');
                    onSubmit({
                      ...search,
                      isActive: ev.target.checked ? '1' : '0',
                    });
                  }}
                />
              }
              label={
                <Typography variant="body2" style={{ fontWeight: 600, color: isActive === '1' ? '#2e7d32' : '#757575' }}>
                  {isActive == 1 ? 'Ativos' : 'Inativos'}
                </Typography>
              }
            />
          </div>

          {/* Tabela Desktop */}
          <Hidden smDown>
            <TableContainer className={classes.tableContainer}>
              <Table>
                <TableHead className={classes.tableHead}>
                  <TableRow>
                    <TableCell className={classes.tableHeadCell}>Usuário</TableCell>
                    <TableCell className={classes.tableHeadCell}>Login</TableCell>
                    <TableCell className={classes.tableHeadCell}>Perfil</TableCell>
                    <TableCell className={classes.tableHeadCell}>CPF</TableCell>
                    <TableCell className={classes.tableHeadCell} align="right">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filterInfo.loading && (
                    <TableRow>
                      <TableCell colSpan={5} align="center" style={{ padding: 40 }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  )}
                  {filterInfo.data &&
                    filterInfo.data.map((data) => (
                      <TableRow
                        onDoubleClick={() => handleModalUpdate(data)}
                        onContextMenu={!mobile ? (ev) => handleClickRight(ev, data) : undefined}
                        style={{ cursor: 'pointer' }}
                        className={classes.tableRow}
                        onClick={mobile ? (ev) => handleClickLeft(ev, data) : undefined}
                        key={data.id}
                      >
                        <TableCell component="th" scope="row">
                          <Box display="flex" alignItems="center">
                            <Avatar className={classes.avatar}>{data.name ? data.name.charAt(0) : ''}</Avatar>
                            <Box>
                              <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                                {fullName(data)}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                ID: {data.id}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{data.username}</Typography>
                        </TableCell>
                        <TableCell>
                          {getProfileChip(data.profile)}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" style={{ fontFamily: 'monospace' }}>
                            {data.cpf}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title={data.isActive == 1 ? "Desativar" : "Ativar"}>
                            <Switch
                              color="primary"
                              checked={data.isActive == 1}
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleModalActive(data);
                              }}
                            />
                          </Tooltip>
                          <Tooltip title="Editar">
                            <IconButton
                              className={classes.actionButton}
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleModalUpdate(data);
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Alterar Senha">
                            <IconButton
                              className={classes.actionButton}
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleModalPassword(data);
                              }}
                            >
                              <VpnKeyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Gerar Link Autenticado">
                            <IconButton
                              className={classes.actionButton}
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenLinkModal(data);
                              }}
                            >
                              <LinkIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}

                  {/* Menus de Contexto */}
                  <Menu
                    keepMounted
                    open={clickRight.mouseY !== null}
                    onClose={handleCloseClickRight}
                    anchorReference="anchorPosition"
                    anchorPosition={
                      clickRight.mouseY !== null && clickRight.mouseX !== null
                        ? { top: clickRight.mouseY, left: clickRight.mouseX }
                        : undefined
                    }
                    PaperProps={{ style: { borderRadius: 12, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' } }}
                  >
                    <MenuItem onClick={() => handleModalUpdate(null, true)}>
                      <EditIcon fontSize="small" style={{ marginRight: 8 }} /> Editar
                    </MenuItem>
                    <MenuItem onClick={() => handleModalPassword(null, true)}>
                      <VpnKeyIcon fontSize="small" style={{ marginRight: 8 }} /> Alterar Senha
                    </MenuItem>
                    <MenuItem onClick={() => { handleOpenLinkModal(modalValues); handleCloseClickRight(); }}>
                      <LinkIcon fontSize="small" style={{ marginRight: 8 }} /> Gerar Link Autenticado
                    </MenuItem>
                    <MenuItem onClick={() => handleModalActive(null, true)}>
                      <Switch size="small" checked={isActive} style={{ marginRight: 8 }} /> {isActive ? 'Desativar' : 'Ativar'}
                    </MenuItem>
                  </Menu>

                  <Menu
                    id="simple-menu"
                    anchorEl={anchorEl}
                    keepMounted
                    open={Boolean(anchorEl)}
                    onClose={() => handleCloseClickLeft()}
                    PaperProps={{ style: { borderRadius: 12 } }}
                  >
                    <MenuItem onClick={() => handleModalUpdate(null, true)}>
                      <EditIcon fontSize="small" style={{ marginRight: 8 }} /> Editar
                    </MenuItem>
                    <MenuItem onClick={() => handleModalPassword(null, true)}>
                      <VpnKeyIcon fontSize="small" style={{ marginRight: 8 }} /> Alterar Senha
                    </MenuItem>
                    <MenuItem onClick={() => { handleOpenLinkModal(modalValues); handleCloseClickLeft(); }}>
                      <LinkIcon fontSize="small" style={{ marginRight: 8 }} /> Gerar Link Autenticado
                    </MenuItem>
                    <MenuItem onClick={() => handleModalActive(null, true)}>
                      Status: {userActive ? 'Ativo' : 'Inativo'}
                    </MenuItem>
                  </Menu>

                  {!filterInfo.loading && (!filterInfo.data || filterInfo.data.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Box py={3} display="flex" flexDirection="column" alignItems="center">
                          <SearchIcon style={{ fontSize: 40, color: '#e0e0e0', marginBottom: 8 }} />
                          <Typography color="textSecondary">Sem resultados encontrados</Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Hidden>

          {/* Listagem em Cards Mobile */}
          <Hidden mdUp>
            <Box style={{ width: '100%', padding: 16 }}>
              {filterInfo.loading && (
                <CircularProgress style={{ margin: '16px auto', display: 'block' }} />
              )}
              {filterInfo.data &&
                filterInfo.data.map((data) => {
                  const isActiveUser = Number(data.isActive) === 1;

                  return (
                    <Card
                      key={data.id}
                      variant="outlined"
                      style={{
                        marginBottom: 12,
                        borderLeft: `4px solid ${isActiveUser ? '#17497F' : '#9E9E9E'}`,
                        borderRadius: 12,
                        backgroundColor: isActiveUser ? '#FFFFFF' : '#FAFAFA',
                      }}
                    >
                      <CardContent style={{ paddingBottom: 8 }}>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          mb={1}
                        >
                          <Box display="flex" alignItems="center">
                            <Avatar className={classes.avatar}>
                              {data.name ? data.name.charAt(0) : ''}
                            </Avatar>
                            <Box>
                              <Typography
                                variant="subtitle1"
                                style={{ fontWeight: 'bold' }}
                              >
                                {fullName(data)}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                ID: #{data.id}
                              </Typography>
                            </Box>
                          </Box>
                          {getProfileChip(data.profile)}
                        </Box>
                        <Divider style={{ margin: '8px 0' }} />
                        <Grid container spacing={1}>
                          <Grid item xs={6}>
                            <Typography
                              variant="caption"
                              color="textSecondary"
                              display="block"
                            >
                              Username / Login
                            </Typography>
                            <Typography variant="body2">{data.username}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography
                              variant="caption"
                              color="textSecondary"
                              display="block"
                            >
                              CPF
                            </Typography>
                            <Typography
                              variant="body2"
                              style={{ fontFamily: 'monospace' }}
                            >
                              {data.cpf || 'Não informado'}
                            </Typography>
                          </Grid>
                        </Grid>
                      </CardContent>
                      <CardActions
                        style={{
                          justifyContent: 'flex-end',
                          paddingTop: 0,
                          paddingRight: 16,
                          paddingBottom: 8,
                        }}
                      >
                        <Tooltip title={isActiveUser ? 'Desativar' : 'Ativar'}>
                          <Switch
                            color="primary"
                            checked={isActiveUser}
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleModalActive(data);
                            }}
                          />
                        </Tooltip>
                        <Tooltip title="Editar">
                          <IconButton
                            className={classes.actionButton}
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleModalUpdate(data);
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Alterar Senha">
                          <IconButton
                            className={classes.actionButton}
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleModalPassword(data);
                            }}
                          >
                            <VpnKeyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Gerar Link Autenticado">
                          <IconButton
                            className={classes.actionButton}
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenLinkModal(data);
                            }}
                          >
                            <LinkIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </CardActions>
                    </Card>
                  );
                })}
              {!filterInfo.loading &&
                (!filterInfo.data || filterInfo.data.length === 0) && (
                  <Box py={3} display="flex" flexDirection="column" alignItems="center">
                    <SearchIcon style={{ fontSize: 40, color: '#e0e0e0', marginBottom: 8 }} />
                    <Typography color="textSecondary">Sem resultados encontrados</Typography>
                  </Box>
                )}
            </Box>
          </Hidden>
        </Card>
      </UIContainer>

      {modalValues && (
        <>
          <Dialog
            open={userActive}
            onClose={() => handleModalActive()}
            fullWidth
            classes={{ paper: classes.dialogPaper }}
          >
            <UIDialogTitle
              id="form-dialog-title"
              onClose={() => handleModalActive()}
            >
              Confirmar alteração de status
            </UIDialogTitle>
            <DialogContent>
              <Typography>
                Deseja realmente <strong>{modalValues.isActive ? 'desativar' : 'ativar'}</strong> o usuário {fullName(modalValues)}?
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => handleModalActive()}>Cancelar</Button>
              <Button onClick={onActive} color="primary" variant="contained" style={{ borderRadius: 8 }}>
                Confirmar
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={passwordModal}
            onClose={() => handleModalPassword()}
            fullWidth
            maxWidth="xs"
            classes={{ paper: classes.dialogPaper }}
          >
            <Formik
              enableReinitialize
              onSubmit={onUpdatePassword}
              initialValues={{ password: '', confirmPassword: '' }}
              validationSchema={passwordSchema}
            >
              {({ errors, touched, submitForm }) => (
                <Form>
                  <UIDialogTitle
                    id="form-dialog-password"
                    onClose={() => handleModalPassword()}
                  >
                    Alterar Senha
                  </UIDialogTitle>
                  <DialogContent>
                    <Box mb={2}>
                      <Typography variant="caption" color="textSecondary">Usuário: {modalValues.username}</Typography>
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Field
                          name="password"
                          label="Nova Senha"
                          type={showPassword ? 'text' : 'password'}
                          fullWidth
                          as={TextField}
                          variant="outlined"
                          error={Boolean(errors.password && touched.password)}
                          helperText={touched.password && errors.password}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => setShowPassword(!showPassword)}
                                  edge="end"
                                  size="small"
                                >
                                  {showPassword ? <Visibility /> : <VisibilityOff />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Field
                          name="confirmPassword"
                          label="Confirmar Senha"
                          type={showPassword ? 'text' : 'password'}
                          fullWidth
                          as={TextField}
                          variant="outlined"
                          error={Boolean(errors.confirmPassword && touched.confirmPassword)}
                          helperText={touched.confirmPassword && errors.confirmPassword}
                        />
                      </Grid>
                    </Grid>
                  </DialogContent>
                  <DialogActions style={{ padding: 20 }}>
                    <Button onClick={() => handleModalPassword()}>Cancelar</Button>
                    <Button
                      onClick={submitForm}
                      color="primary"
                      variant="contained"
                      style={{ borderRadius: 8 }}
                    >
                      Salvar Senha
                    </Button>
                  </DialogActions>
                </Form>
              )}
            </Formik>
          </Dialog>

          <Dialog
            open={view}
            onClose={() => {
              handleModalUpdate();
              setModalValues(null);
            }}
            aria-labelledby="form-dialog-title"
            classes={{ paper: classes.dialogPaper }}
            maxWidth="sm"
            fullWidth
          >
            <Formik
              enableReinitialize
              onSubmit={onUpdate}
              initialValues={modalValues}
            >
              {({ values, errors, setFieldValue, submitForm }) => (
                <Form>
                  <UIDialogTitle
                    id="form-dialog-title"
                    onClose={() => handleModalUpdate()}
                  >
                    Editar Usuário
                  </UIDialogTitle>
                  <DialogContent>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Field
                          name="name"
                          label="Nome"
                          className={disabledTextField}
                          placeholder="Nome"
                          fullWidth
                          as={TextField}
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Field
                          name="lastName"
                          label="Sobrenome"
                          placeholder="Sobrenome"
                          className={disabledTextField}
                          fullWidth
                          as={TextField}
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <InputMask
                          disabled
                          value={values.cpf}
                          mask="999.999.999-99"
                          onChange={(ev) =>
                            setFieldValue(
                              'cpf',
                              ev.target.value.replace(/[^0-9]/g, ''),
                            )
                          }
                        >
                          {() => (
                            <TextField
                              label="CPF"
                              className={disabledTextField}
                              fullWidth
                              variant="outlined"
                            />
                          )}
                        </InputMask>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Field
                          name="username"
                          label="Login"
                          className={disabledTextField}
                          disabled
                          fullWidth
                          as={TextField}
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormControl fullWidth variant="outlined">
                          <InputLabel shrink id="users-edit-profile">
                            Perfil
                          </InputLabel>
                          <Field
                            name="profile"
                            labelId="users-edit-profile"
                            disabled={profileInfo.loading}
                            displayEmpty
                            fullWidth
                            as={Select}
                            label="Perfil"
                          >
                            <MenuItem disabled value="">
                              <em>Informe o perfil do usuário</em>
                            </MenuItem>
                            {profileInfo.data &&
                              profileInfo.data.map((prof) => (
                                <MenuItem key={prof.id} value={prof.id}>
                                  {prof.name}
                                </MenuItem>
                              ))}
                          </Field>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <Autocomplete
                          multiple
                          disabled={cashierInfo.loading}
                          options={cashierInfo.data || []}
                          value={values.cashRegisters}
                          getOptionLabel={(cash) => (cash && cash.name) ? cash.name : ''}
                          onChange={(event, cash) =>
                            setFieldValue('cashRegisters', cash)
                          }
                          getOptionSelected={(op, cash) => op.name === (cash ? cash.name : '')}
                          noOptionsText="Sem opções"
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              error={Boolean(errors.cashRegisters)}
                              label="Operador do caixa"
                              placeholder="Adicionar mais"
                              variant="outlined"
                            />
                          )}
                        />
                      </Grid>
                    </Grid>
                  </DialogContent>
                  <DialogActions style={{ padding: 20, justifyContent: 'space-between' }}>
                    <Button
                      onClick={() => {
                        setView(false);
                        setPasswordModal(true);
                        setShowPassword(false);
                      }}
                      startIcon={<VpnKeyIcon />}
                      color="secondary"
                    >
                      Alterar Senha
                    </Button>
                    <Box>
                      {JSON.stringify(values) !== JSON.stringify(modalValues) && (
                        <Button
                          onClick={() => handleModalUpdate()}
                          color="primary"
                          style={{ marginRight: 8 }}
                        >
                          Cancelar
                        </Button>
                      )}
                      <Button
                        onClick={
                          JSON.stringify(values) === JSON.stringify(modalValues)
                            ? () => handleModalUpdate()
                            : () => submitForm()
                        }
                        color="primary"
                        variant="contained"
                        style={{ borderRadius: 8 }}
                      >
                        {JSON.stringify(values) === JSON.stringify(modalValues)
                          ? 'Fechar'
                          : 'Salvar'}
                      </Button>
                    </Box>
                  </DialogActions>
                </Form>
              )}
            </Formik>
          </Dialog>
        </>
      )}
      <UIDialogConfirm
        open={alert}
        close={() => setAlert(false)}
        data={activeInfo}
        setrequestinfo={closeAlert}
        messageSucess="Operação realizada com sucesso"
        type="success"
      />
      <UIDialogConfirm
        open={alert}
        close={() => setAlert(false)}
        data={updateInfo}
        setrequestinfo={closeAlert}
        messageSucess={
          updateInfo.data
            ? updateInfo.data.message
            : 'Usuário atualizado com sucesso'
        }
        type="success"
      />
      <UIDialogConfirm
        open={alert}
        close={() => setAlert(false)}
        data={changePassInfo}
        setrequestinfo={closeAlert}
        messageSucess="Senha alterada com sucesso!"
        type="success"
      />

      {/* Modal de Link Autenticado Gerado */}
      <Dialog
        open={linkModal}
        onClose={() => setLinkModal(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ style: { borderRadius: 16 } }}
      >
        <UIDialogTitle
          id="form-dialog-link"
          onClose={() => setLinkModal(false)}
        >
          Link de Acesso Rápido
        </UIDialogTitle>
        <DialogContent style={{ paddingTop: 8 }}>
          {/* Cartão de Identificação do Usuário */}
          <Box
            display="flex"
            alignItems="center"
            p={1.5}
            mb={2.5}
            bgcolor="#f8fafc"
            borderRadius={10}
            border="1px solid #e2e8f0"
          >
            <Avatar
              style={{
                backgroundColor: '#1976d2',
                color: '#ffffff',
                fontWeight: 600,
                width: 38,
                height: 38,
                marginRight: 12,
              }}
            >
              {targetUser?.name?.[0]?.toUpperCase() || <PersonIcon />}
            </Avatar>
            <Box flexGrow={1} minWidth={0}>
              <Typography variant="subtitle2" noWrap style={{ fontWeight: 600, color: '#1e293b' }}>
                {targetUser?.name} {targetUser?.lastName}
              </Typography>
              <Typography variant="caption" noWrap color="textSecondary" style={{ display: 'block' }}>
                @{targetUser?.username} • {targetUser?.profile_name || 'Usuário'}
              </Typography>
            </Box>
          </Box>

          {/* Seletor de Validade */}
          <Box mb={2}>
            <Box display="flex" alignItems="center" mb={1}>
              <ScheduleIcon style={{ fontSize: 18, color: '#64748b', marginRight: 6 }} />
              <Typography variant="body2" style={{ fontWeight: 600, color: '#334155' }}>
                Validade do Link:
              </Typography>
            </Box>

            {/* Quick chips para durações mais comuns */}
            <Box display="flex" flexWrap="wrap" style={{ gap: 6 }} mb={1.5}>
              {DURATION_OPTIONS.slice(0, 5).map((opt) => {
                const isSelected = linkDuration === opt.value;
                return (
                  <Chip
                    key={opt.value}
                    size="small"
                    label={opt.chipLabel}
                    clickable
                    onClick={() => {
                      setLinkDuration(opt.value);
                    }}
                    color={isSelected ? 'primary' : 'default'}
                    variant={isSelected ? 'default' : 'outlined'}
                    style={{
                      fontWeight: isSelected ? 600 : 500,
                      borderRadius: 8,
                      fontSize: '0.78rem',
                      borderColor: isSelected ? undefined : '#cbd5e1',
                    }}
                  />
                );
              })}
            </Box>

            <FormControl variant="outlined" size="small" fullWidth>
              <InputLabel id="link-duration-label">Duração de Validade</InputLabel>
              <Select
                labelId="link-duration-label"
                value={linkDuration}
                onChange={(e) => setLinkDuration(e.target.value)}
                label="Duração de Validade"
                style={{ borderRadius: 8 }}
              >
                {DURATION_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {linkDuration === 'custom' && (
              <Box mt={1.5} display="flex" style={{ gap: 8 }}>
                <TextField
                  label="Quantidade"
                  type="number"
                  size="small"
                  variant="outlined"
                  value={customDurationValue}
                  onChange={(e) => setCustomDurationValue(Math.max(1, parseInt(e.target.value, 10) || 1))}
                  inputProps={{ min: 1, max: 720 }}
                  style={{ width: '45%' }}
                />
                <FormControl variant="outlined" size="small" style={{ width: '55%' }}>
                  <InputLabel id="custom-unit-label">Unidade</InputLabel>
                  <Select
                    labelId="custom-unit-label"
                    value={customDurationUnit}
                    onChange={(e) => setCustomDurationUnit(e.target.value)}
                    label="Unidade"
                  >
                    <MenuItem value="minutes">Minuto(s)</MenuItem>
                    <MenuItem value="hours">Hora(s)</MenuItem>
                    <MenuItem value="days">Dia(s)</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            )}
          </Box>

          {/* Se ainda não gerou, botão principal de geração */}
          {!generatedUrl ? (
            <Box mt={2.5} mb={1}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                size="large"
                disabled={generateLinkInfo.loading}
                onClick={() => handleGenerateLink()}
                startIcon={
                  generateLinkInfo.loading ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <LinkIcon />
                  )
                }
                style={{
                  borderRadius: 8,
                  padding: '10px 16px',
                  fontWeight: 600,
                  textTransform: 'none',
                  boxShadow: 'none',
                }}
              >
                {generateLinkInfo.loading ? 'Gerando Link...' : 'Gerar Link de Acesso'}
              </Button>
            </Box>
          ) : (
            <Box mt={2}>
              <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>
                Link Gerado:
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                value={generatedUrl}
                InputProps={{
                  readOnly: true,
                  style: {
                    fontFamily: 'monospace',
                    fontSize: '0.82rem',
                    backgroundColor: '#f8fafc',
                    borderRadius: 8,
                  },
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={copied ? 'Copiado!' : 'Copiar Link'}>
                        <IconButton
                          onClick={() => {
                            navigator.clipboard.writeText(generatedUrl);
                            setCopied(true);
                          }}
                          edge="end"
                          size="small"
                        >
                          <FileCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />

              <Box mt={1.5} display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap">
                <Chip
                  size="small"
                  icon={<ScheduleIcon style={{ fontSize: 15, color: '#15803d' }} />}
                  label={`Válido por ${getDurationLabel(generatedDuration)}`}
                  style={{
                    backgroundColor: '#f0fdf4',
                    color: '#15803d',
                    fontWeight: 600,
                    border: '1px solid #bbf7d0',
                    fontSize: '0.75rem',
                  }}
                />
                {copied && (
                  <Typography
                    variant="caption"
                    style={{ color: '#15803d', fontWeight: 600, display: 'flex', alignItems: 'center' }}
                  >
                    <CheckCircleOutlineIcon style={{ fontSize: 15, marginRight: 3 }} />
                    Copiado!
                  </Typography>
                )}
              </Box>

              <Box mt={1.5} textAlign="center">
                <Button
                  size="small"
                  color="primary"
                  disabled={generateLinkInfo.loading}
                  onClick={() => handleGenerateLink()}
                  startIcon={generateLinkInfo.loading ? <CircularProgress size={14} /> : <LinkIcon />}
                  style={{ textTransform: 'none', fontSize: '0.8rem', color: '#1976d2' }}
                >
                  {generateLinkInfo.loading ? 'Atualizando...' : 'Gerar novo link com validade selecionada'}
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions style={{ padding: '14px 24px', justifyContent: 'space-between', borderTop: '1px solid #f1f5f9' }}>
          <Button
            onClick={() => setLinkModal(false)}
            color="default"
            style={{ borderRadius: 8, textTransform: 'none' }}
          >
            Fechar
          </Button>

          {generatedUrl ? (
            <Box display="flex" style={{ gap: 8 }}>
              <Button
                onClick={() => {
                  navigator.clipboard.writeText(generatedUrl);
                  setCopied(true);
                }}
                color="primary"
                variant="outlined"
                startIcon={<FileCopyIcon />}
                style={{ borderRadius: 8, textTransform: 'none', fontWeight: 600 }}
              >
                {copied ? 'Copiado!' : 'Copiar Link'}
              </Button>
              <Button
                href={generatedUrl}
                target="_blank"
                color="primary"
                variant="contained"
                style={{ borderRadius: 8, textTransform: 'none', fontWeight: 600, boxShadow: 'none' }}
              >
                Abrir Link
              </Button>
            </Box>
          ) : null}
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default UserSearch;